import type { Visibility } from './types';

/**
 * How the five privacy settings are put to a person: the same words on a
 * recording's own page and in Settings, so choosing one in either place means
 * the same thing.
 *
 * The scale runs from the whole internet down to nobody, and the copy is
 * written to be read in that order: each line says who is let in, and the
 * detail says what it takes to be one of them.
 */

export interface PrivacyOption {
  value: Visibility;
  label: string;
  detail: (teamName: string | null) => string;
}

export const PRIVACY_OPTIONS: PrivacyOption[] = [
  {
    value: 'public',
    label: 'Anyone with the link',
    detail: () => 'No account needed. Wherever the link goes, so does the recording',
  },
  {
    value: 'signedIn',
    label: 'Anyone signed in to Slate',
    detail: () => 'The link opens it, but only for someone with a Slate account',
  },
  {
    value: 'team',
    label: 'Your team',
    detail: (teamName) =>
      teamName ? `Everyone on ${teamName}, and nobody outside it` : 'Everyone on your team, and nobody outside it',
  },
  {
    value: 'invited',
    label: 'Only people you name',
    detail: () => 'The addresses you list, once they sign in with them',
  },
  {
    value: 'private',
    label: 'Just you',
    detail: () => 'Nobody else opens it. Not your team, not its admins',
  },
];

/**
 * The four a default may name. A list of addresses belongs to a recording,
 * not to every recording someone will ever make, so `invited` is chosen on
 * the recording itself.
 */
export const DEFAULTABLE_PRIVACY = PRIVACY_OPTIONS.filter((option) => option.value !== 'invited');

export function privacyLabel(visibility: Visibility): string {
  return PRIVACY_OPTIONS.find((option) => option.value === visibility)?.label ?? 'Anyone with the link';
}

/** The same thing in as few words as a library card has room for. */
export function privacyShortLabel(visibility: Visibility): string {
  switch (visibility) {
    case 'public':
      return 'Anyone with the link';
    case 'signedIn':
      return 'Signed in to Slate';
    case 'team':
      return 'Your team';
    case 'invited':
      return 'People you named';
    case 'private':
      return 'Just you';
  }
}
